import type { RoleLevel } from '@/src/lib/navigation';

const SUPERUSER_ROLES = new Set([
  'System Owner',
  'system_owner',
  'system_admin',
  'System Admin',
  'Superuser',
  'Superuser (Sysadmin)',
]);
const ADMIN_ROLES = new Set(['Tenant Owner', 'tenant_owner', 'Tenant Admin', 'tenant_admin', 'Administrator']);
const MANAGER_ROLES = new Set(['Buchhalter', 'accountant', 'Manager', 'Steuerberater Vollzugriff', 'Steuerberater Vorbereitung', 'tax_advisor_prep']);
const READONLY_ROLES = new Set([
  'Steuerberater Lesen',
  'tax_advisor',
  'Nur Lesen',
  'readonly',
  'Read-Only',
  'Readonly',
  'Viewer',
  'Beobachter',
]);

/**
 * Mappt die Rollen-Namen eines Users auf den höchsten RoleLevel.
 * - 'superuser' = System Owner, System Admin
 * - 'admin'     = Tenant Owner, Tenant Admin
 * - 'manager'   = Buchhalter
 * - 'user'      = Alles andere (z. B. Sachbearbeiter)
 * - 'readonly'  = Read-Only-Rollen
 *
 * Wenn ein User mehrere Rollen hat, gewinnt der höchste Level.
 */
export function deriveRoleLevel(roles: readonly string[] | null | undefined): RoleLevel {
  if (!roles || roles.length === 0) return 'readonly';
  if (roles.some((r) => SUPERUSER_ROLES.has(r))) return 'superuser';
  if (roles.some((r) => ADMIN_ROLES.has(r))) return 'admin';
  if (roles.some((r) => MANAGER_ROLES.has(r))) return 'manager';
  if (roles.every((r) => READONLY_ROLES.has(r))) return 'readonly';
  return 'user';
}

const RANK: Record<RoleLevel, number> = {
  readonly: 1,
  user: 2,
  manager: 3,
  admin: 4,
  superuser: 5,
};

export function hasRoleLevel(
  roles: readonly string[] | null | undefined,
  required: RoleLevel,
): boolean {
  return RANK[deriveRoleLevel(roles)] >= RANK[required];
}

/**
 * Liefert true, wenn der User die Rolle SUPER_ADMIN innehat (Power-Mode).
 */
export function isSuperuser(roles: readonly string[] | null | undefined): boolean {
  return deriveRoleLevel(roles) === 'superuser';
}

/**
 * Liefert true, wenn der User mehrere unterschiedliche RoleLevel-Rollen innehat.
 * In diesem Fall lohnt sich der Rollen-Kontext-Switch in der Topbar.
 */
export function hasMultipleRoleLevels(roles: readonly string[] | null | undefined): boolean {
  if (!roles || roles.length < 2) return false;
  const levels = new Set(roles.map((r) => deriveRoleLevel([r])));
  return levels.size > 1;
}

export const ROLE_LEVEL_LABELS: Record<RoleLevel, string> = {
  superuser: 'Sysadmin',
  admin: 'Administrator',
  manager: 'Manager',
  user: 'Benutzer',
  readonly: 'Lesezugriff',
};
